import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Refresh, Plus, Trash2, Settings } from "@/components/icons";

import {
  fetchLoadBalancingGroups,
  createLoadBalancingGroup,
  updateLoadBalancingGroup,
  deleteLoadBalancingGroup,
  addUnitToGroup,
  removeUnitFromGroup,
  fetchGroupStatus,
  LBGroup,
} from "@/lib/api";
import { useAuth } from "@/app/AuthContext";
import { useDashboardOrg } from "@/app/DashboardOrgContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Modal } from "@/components/Modal";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { formatNumber } from "@voltie/localization-web";
import { useT } from "@/app/localization";
import { errorMessage } from "@/app/serverErrors";
import { HttpError } from "@/lib/http";

function utilColor(pct: number): string {
  if (pct <= 70) return "progress-success";
  if (pct <= 90) return "progress-warning";
  return "progress-error";
}

export function LoadBalancingPage() {
  const { isFree } = useAuth();
  const t = useT();

  // Free-tier gate
  if (isFree) {
    return (
      <div className="space-y-5 pt-[86px]">
        <h1 className="text-2xl font-semibold">{t("operator.nav.load_balancing")}</h1>
        <Card>
          <CardContent className="items-center text-center">
            <div className="text-4xl">⚡</div>
            <div className="text-lg font-semibold">{t("operator.orgs.lb.prime_title")}</div>
            <div className="opacity-70">
              {t("operator.orgs.lb.prime_body")}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <LoadBalancingContent />;
}

function LoadBalancingContent() {
  const t = useT();
  const qc = useQueryClient();
  // Manager → org-scoped LB (/api/org/<id>/load-balancing/...); admin → fleet. orgId
  // is in every query key so switching org refetches; scopeReady gates the manager
  // pre-selection window so no fleet request is issued.
  const { dashboardOrgId, scopeReady } = useDashboardOrg();
  // Deep-link target from the unit page's LB badge (?group=<id>): scroll to and
  // briefly highlight that group's card so an admin lands right on it.
  const [searchParams] = useSearchParams();
  const highlightGroupId = searchParams.get("group");
  const [createOpen, setCreateOpen] = useState(false);
  const [editGroup, setEditGroup] = useState<LBGroup | null>(null);
  const [addUnitOpen, setAddUnitOpen] = useState<string | null>(null);
  const [newUnitId, setNewUnitId] = useState("");

  // Create form
  const [formId, setFormId] = useState("");
  const [formName, setFormName] = useState("");
  const [formMaxAmps, setFormMaxAmps] = useState("32");
  const [formMinAmps, setFormMinAmps] = useState("6");

  // §10 — on an org SWITCH (not initial mount), drop ALL per-org transient UI and drop
  // the PREVIOUS org's cached/in-flight LB queries so a dialog or mutation can never
  // target a group from the old org under the new org's URL. We scope the cancel to the
  // previous org id so the freshly-mounted new-org query is left to resolve. Group
  // lists/status are keyed by dashboardOrgId, so the new org's data (or an empty loading
  // state) replaces the old — no Org A groups are shown while Org B is selected.
  const prevOrgRef = useRef(dashboardOrgId);
  useEffect(() => {
    const prev = prevOrgRef.current;
    prevOrgRef.current = dashboardOrgId;
    if (prev === dashboardOrgId) return;
    setCreateOpen(false);
    setEditGroup(null);
    setAddUnitOpen(null);
    setNewUnitId("");
    setFormId(""); setFormName(""); setFormMaxAmps("32"); setFormMinAmps("6");
    qc.cancelQueries({ queryKey: ["load-balancing", prev] });
    qc.removeQueries({ queryKey: ["load-balancing", prev] });
  }, [dashboardOrgId, qc]);

  const groupsQ = useQuery({
    queryKey: ["load-balancing", dashboardOrgId, "groups"],
    queryFn: () => fetchLoadBalancingGroups(dashboardOrgId),
    enabled: scopeReady,
    refetchInterval: 5_000,
  });

  const createM = useMutation({
    mutationFn: () => createLoadBalancingGroup({
      group_id: formId,
      name: formName,
      max_amps: parseFloat(formMaxAmps) || 32,
      min_amps: parseFloat(formMinAmps) || 6,
    }, dashboardOrgId),
    onSuccess: () => {
      toast.success(t("operator.orgs.lb.group_created"));
      setCreateOpen(false);
      setFormId(""); setFormName(""); setFormMaxAmps("32"); setFormMinAmps("6");
      qc.invalidateQueries({ queryKey: ["load-balancing"] });
    },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.orgs.lb.create_failed" })),
  });

  const updateM = useMutation({
    mutationFn: (patch: { groupId: string; name?: string; max_amps?: number; min_amps?: number }) =>
      updateLoadBalancingGroup(patch.groupId, { name: patch.name, max_amps: patch.max_amps, min_amps: patch.min_amps }, dashboardOrgId),
    onSuccess: () => {
      toast.success(t("operator.orgs.lb.group_updated"));
      setEditGroup(null);
      qc.invalidateQueries({ queryKey: ["load-balancing"] });
    },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.orgs.lb.update_failed" })),
  });

  const deleteM = useMutation({
    mutationFn: (groupId: string) => deleteLoadBalancingGroup(groupId, dashboardOrgId),
    onSuccess: () => {
      toast.success(t("operator.orgs.lb.group_deleted"));
      qc.invalidateQueries({ queryKey: ["load-balancing"] });
    },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.orgs.lb.delete_failed" })),
  });

  const addUnitM = useMutation({
    mutationFn: ({ groupId, unitId }: { groupId: string; unitId: string }) => addUnitToGroup(groupId, unitId, dashboardOrgId),
    onSuccess: () => {
      toast.success(t("operator.orgs.unit_added"));
      setAddUnitOpen(null);
      setNewUnitId("");
      qc.invalidateQueries({ queryKey: ["load-balancing"] });
    },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.orgs.add_unit_failed" })),
  });

  const removeUnitM = useMutation({
    mutationFn: ({ groupId, unitId }: { groupId: string; unitId: string }) => removeUnitFromGroup(groupId, unitId, dashboardOrgId),
    onSuccess: () => {
      toast.success(t("operator.orgs.unit_removed"));
      qc.invalidateQueries({ queryKey: ["load-balancing"] });
    },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.orgs.lb.remove_unit_failed" })),
  });

  const groups: LBGroup[] = groupsQ.data?.groups ?? [];

  return (
    <div className="space-y-5 pt-[86px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t("operator.nav.load_balancing")}</h1>
          <div className="text-sm opacity-70">{t("operator.orgs.lb.subtitle")}</div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => groupsQ.refetch()}>
            <Refresh className="h-4 w-4" /> {t("common.action.refresh")}
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" /> {t("operator.orgs.lb.new_group")}
          </Button>
        </div>
      </div>

      {groupsQ.isLoading ? (
        <div className="flex justify-center p-12">
          <span className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" />
        </div>
      ) : groupsQ.isError ? (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {groupsQ.error instanceof HttpError && groupsQ.error.status === 503
            ? t("operator.orgs.lb.not_enabled")
            : t("operator.orgs.lb.load_failed")}
        </div>
      ) : groups.length === 0 ? (
        <Card>
          <CardContent className="items-center text-center">
            <div className="text-lg font-semibold">{t("operator.orgs.lb.empty_title")}</div>
            <div className="opacity-70">{t("operator.orgs.lb.empty_body")}</div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {groups.map((g) => (
            <GroupCard
              key={g.group_id}
              group={g}
              highlight={g.group_id === highlightGroupId}
              onEdit={() => setEditGroup(g)}
              onDelete={() => { if (confirm(t("operator.orgs.lb.delete_confirm", { name: g.name }))) deleteM.mutate(g.group_id); }}
              onAddUnit={() => { setAddUnitOpen(g.group_id); setNewUnitId(""); }}
              onRemoveUnit={(unitId) => removeUnitM.mutate({ groupId: g.group_id, unitId })}
            />
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title={t("operator.orgs.lb.create_title")}>
        <div className="space-y-3">
          <div>
            <Label>{t("operator.orgs.lb.group_id")}</Label>
            <Input className="mt-1" value={formId} onChange={(e) => setFormId(e.target.value)} placeholder={t("operator.orgs.lb.group_id_placeholder")} />
          </div>
          <div>
            <Label>{t("operator.orgs.lb.name")}</Label>
            <Input className="mt-1" value={formName} onChange={(e) => setFormName(e.target.value)} placeholder={t("operator.orgs.lb.name_placeholder")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("operator.orgs.lb.max_amps")}</Label>
              <Input className="mt-1" type="number" value={formMaxAmps} onChange={(e) => setFormMaxAmps(e.target.value)} />
            </div>
            <div>
              <Label>{t("operator.orgs.lb.min_amps_per_unit")}</Label>
              <Input className="mt-1" type="number" value={formMinAmps} onChange={(e) => setFormMinAmps(e.target.value)} />
            </div>
          </div>
          <Button className="w-full" onClick={() => createM.mutate()} disabled={createM.isPending || !formId || !formName}>
            {createM.isPending ? t("operator.orgs.lb.creating") : t("operator.orgs.lb.create_button")}
          </Button>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal open={!!editGroup} onClose={() => setEditGroup(null)} title={t("operator.orgs.lb.edit_title", { name: editGroup?.name ?? "" })}>
        {editGroup && (
          <EditGroupForm
            group={editGroup}
            onSave={(patch) => updateM.mutate({ groupId: editGroup.group_id, ...patch })}
            isPending={updateM.isPending}
          />
        )}
      </Modal>

      {/* Add Unit Modal */}
      <Modal open={!!addUnitOpen} onClose={() => setAddUnitOpen(null)} title={t("operator.orgs.access.add_unit_title")}>
        <div className="space-y-3">
          <div>
            <Label>{t("operator.units.column_unit_id")}</Label>
            <Input className="mt-1" value={newUnitId} onChange={(e) => setNewUnitId(e.target.value)} placeholder={t("operator.orgs.lb.unit_id_placeholder")} />
          </div>
          <Button
            className="w-full"
            onClick={() => addUnitOpen && addUnitM.mutate({ groupId: addUnitOpen, unitId: newUnitId })}
            disabled={addUnitM.isPending || !newUnitId}
          >
            {addUnitM.isPending ? t("operator.orgs.lb.adding") : t("operator.orgs.lb.add_unit")}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function GroupCard({
  group,
  highlight = false,
  onEdit,
  onDelete,
  onAddUnit,
  onRemoveUnit,
}: {
  group: LBGroup;
  highlight?: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onAddUnit: () => void;
  onRemoveUnit: (unitId: string) => void;
}) {
  const t = useT();
  const { dashboardOrgId, scopeReady } = useDashboardOrg();
  // Scroll the deep-linked card into view once (group id is stable), so a jump
  // from a unit's LB badge lands on the right card even below the fold.
  const cardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (highlight && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlight]);
  const statusQ = useQuery({
    queryKey: ["load-balancing", dashboardOrgId, "groups", group.group_id, "status"],
    queryFn: () => fetchGroupStatus(group.group_id, dashboardOrgId),
    enabled: scopeReady,
    refetchInterval: 5_000,
  });

  const status = statusQ.data;
  const utilPct = status?.utilization_pct ?? 0;
  const headroom = status?.headroom ?? group.max_amps;
  const totalDraw = status?.total_current_draw ?? 0;
  const phantomIds = status?.phantom_ids ?? [];
  const phantomCount = status?.phantom_count ?? 0;

  return (
    <div ref={cardRef} className="scroll-mt-24">
      <Card className={highlight ? "ring-2 ring-primary ring-offset-2 ring-offset-base-100" : undefined}>
      <CardContent>
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <CardTitle>{group.name}</CardTitle>
              {phantomCount > 0 && (
                <Badge variant="error" className="text-[10px] h-4 px-1.5">
                  {t("operator.orgs.lb.phantom_count", { count: phantomCount })}
                </Badge>
              )}
            </div>
            <div className="text-xs font-mono opacity-60">{group.group_id}</div>
          </div>
          <div className="flex gap-1">
            <Button data-testid={`lb-edit-${group.group_id}`} variant="ghost" size="xs" onClick={onEdit} aria-label={t("operator.orgs.lb.edit_group")}><Settings className="h-3 w-3" /></Button>
            <Button data-testid={`lb-delete-${group.group_id}`} variant="ghost" size="xs" className="text-error" onClick={onDelete} aria-label={t("operator.orgs.lb.delete_group")}><Trash2 className="h-3 w-3" /></Button>
          </div>
        </div>

        {phantomCount > 0 && (
          <div className="mt-2 rounded border border-error/50 bg-error/10 p-2 text-xs">
            <div className="font-semibold text-error mb-1">
              {t("operator.orgs.lb.phantom_warning", { amps: String(group.min_amps) })}
            </div>
            {phantomIds.map((pid) => (
              <div key={pid} className="flex items-center justify-between py-0.5">
                <span className="font-mono opacity-80 break-all">{pid}</span>
                <Button variant="ghost" size="xs" className="text-error" onClick={() => onRemoveUnit(pid)}>{t("common.action.remove")}</Button>
              </div>
            ))}
          </div>
        )}

        {/* Utilization */}
        <div className="mt-3">
          <div className="flex justify-between text-xs mb-1">
            <span>{t("operator.orgs.lb.draw", { draw: formatNumber(totalDraw, 1), max: String(group.max_amps) })}</span>
            <span>{t("operator.orgs.lb.headroom", { amps: formatNumber(headroom, 1) })}</span>
          </div>
          <progress
            className={`progress w-full ${utilColor(utilPct)}`}
            value={utilPct}
            max={100}
          />
          <div className="text-right text-xs opacity-60">{t("operator.orgs.lb.utilized", { percent: formatNumber(utilPct, 0) })}</div>
        </div>

        {/* Units */}
        <div className="mt-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">{t("operator.orgs.units_heading", { count: group.unit_ids?.length ?? 0 })}</span>
            <Button data-testid={`lb-addunit-${group.group_id}`} variant="ghost" size="xs" onClick={onAddUnit}>
              <Plus className="h-3 w-3" /> {t("common.action.add")}
            </Button>
          </div>
          {status?.units?.length ? (
            <div className="space-y-1">
              {status.units.map((u) => {
                const showIdLine = !!u.name && u.name !== u.unit_id;
                return (
                  <div key={u.unit_id} className="flex items-center justify-between p-2 rounded bg-card/50 text-sm">
                    <Link
                      to={`/unit/${encodeURIComponent(u.unit_id)}`}
                      state={{ backTo: "/load-balancing", backLabelKey: "operator.nav.load_balancing" }}
                      className="flex min-w-0 items-center gap-2 hover:underline"
                    >
                      <Badge variant={u.is_online ? "success" : "neutral"} className="text-[10px] h-4 px-1.5">
                        {t("operator.orgs.lb.unit_online", { state: u.is_online ? "on" : "off" })}
                      </Badge>
                      <div className="flex min-w-0 flex-col leading-tight">
                        <span className="truncate text-xs">{u.name || u.unit_id}</span>
                        {showIdLine && (
                          <span className="truncate font-mono text-[10px] opacity-60">{u.unit_id}</span>
                        )}
                      </div>
                    </Link>
                    <div className="flex items-center gap-2">
                      <span className="text-xs">{t("operator.orgs.lb.unit_draw", { draw: u.current_draw != null ? formatNumber(u.current_draw, 1) : "0", allocated: formatNumber(u.allocated_amps ?? u.allocated ?? 0, 1) })}</span>
                      <Button data-testid={`lb-rm-${u.unit_id}`} variant="ghost" size="xs" className="text-error" onClick={() => onRemoveUnit(u.unit_id)}
                        aria-label={t("operator.orgs.remove_unit", { unitId: u.unit_id })}>×</Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : group.unit_ids?.length ? (
            <div className="space-y-1">
              {group.unit_ids.map((uid) => (
                <div key={uid} className="flex items-center justify-between p-2 rounded bg-card/50 text-sm">
                  <Link
                    to={`/unit/${encodeURIComponent(uid)}`}
                    state={{ backTo: "/load-balancing", backLabelKey: "operator.nav.load_balancing" }}
                    className="min-w-0 truncate font-mono text-xs hover:underline"
                  >
                    {uid}
                  </Link>
                  <Button data-testid={`lb-rm-${uid}`} variant="ghost" size="xs" className="text-error" onClick={() => onRemoveUnit(uid)}
                    aria-label={t("operator.orgs.remove_unit", { unitId: uid })}>×</Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs opacity-50">{t("operator.orgs.lb.no_units")}</div>
          )}
        </div>

        {/* Last recalculation */}
        <div className="mt-2 text-xs opacity-50">
          {/* last_recalculation is naive server-local time: display unchanged (LOC-405 report). */}
          {t("operator.orgs.lb.last_recalculation", {
            time: status?.last_recalculation ? new Date(status.last_recalculation).toLocaleTimeString() : t("operator.users.never"),
          })}
        </div>
      </CardContent>
      </Card>
    </div>
  );
}

function EditGroupForm({
  group,
  onSave,
  isPending,
}: {
  group: LBGroup;
  onSave: (patch: { name?: string; max_amps?: number; min_amps?: number }) => void;
  isPending: boolean;
}) {
  const t = useT();
  const [name, setName] = useState(group.name);
  const [maxAmps, setMaxAmps] = useState(String(group.max_amps));
  const [minAmps, setMinAmps] = useState(String(group.min_amps));

  return (
    <div className="space-y-3">
      <div>
        <Label>{t("operator.orgs.lb.name")}</Label>
        <Input className="mt-1" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>{t("operator.orgs.lb.max_amps")}</Label>
          <Input className="mt-1" type="number" value={maxAmps} onChange={(e) => setMaxAmps(e.target.value)} />
        </div>
        <div>
          <Label>{t("operator.orgs.lb.min_amps")}</Label>
          <Input className="mt-1" type="number" value={minAmps} onChange={(e) => setMinAmps(e.target.value)} />
        </div>
      </div>
      <Button
        className="w-full"
        onClick={() => onSave({ name, max_amps: parseFloat(maxAmps), min_amps: parseFloat(minAmps) })}
        disabled={isPending}
      >
        {isPending ? t("common.state.saving") : t("common.action.save_changes")}
      </Button>
    </div>
  );
}
